import { useEffect, useState, useCallback } from "react";
import {
  HiOutlinePlus,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineSearch,
  HiOutlineExclamation,
  HiOutlineChevronLeft,
  HiOutlineChevronDown,
  HiOutlineChevronRight,
  HiOutlineX,
  HiOutlinePhotograph,
  HiOutlineClock,
} from "react-icons/hi";
import {
  fetchCompetencies,
  fetchSetsForCompetency,
  createSet,
  updateSet,
  deleteSet,
  fetchQuestionsForSet,
  createQuestion,
  updateQuestion,
  deleteQuestion,
  uploadSetResource,
} from "../services/masterDataApi";
import Toast from "../components/Toast";
import { useUserRole } from "../hooks/useUserRole";

/* ── style tokens ─────────────────────────────────────────────── */
const inputClass =
  "w-full bg-white border border-[#d0d7de] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0078d4]/20 focus:border-[#0078d4]";
const selectClass =
  "w-full bg-white border border-[#d0d7de] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0078d4]/20 focus:border-[#0078d4]";
const labelClass = "block text-sm font-medium text-[#24292f] mb-1";
const btnPrimary =
  "flex items-center gap-1.5 px-4 py-2 bg-[#0078d4] text-white text-xs font-medium rounded-lg hover:bg-[#106ebe] transition-all";
const btnSecondary =
  "px-4 py-2 text-sm font-medium text-[#24292f] bg-white border border-[#d0d7de] rounded-lg hover:bg-[#f3f4f6] transition-all";
const tagColors = {
  Instructor: "bg-orange-50 text-orange-700 border-orange-200",
  Student: "bg-green-50 text-green-700 border-green-200",
  Rules: "bg-purple-50 text-purple-700 border-purple-200",
};

/* ── instruction tag renderer ─────────────────────────────────── */
function renderInstructionPreview(text) {
  if (!text) return null;
  const parts = text.split(/(\[(?:Instructor|Student|Rules)\][^[]*(?:\[\/(?:Instructor|Student|Rules)\])?)/g);
  return parts.map((part, i) => {
    const match = part.match(/^\[(Instructor|Student|Rules)\](.*?)(?:\[\/(?:Instructor|Student|Rules)\])?$/s);
    if (match) {
      const tag = match[1];
      const content = match[2].trim();
      return (
        <span key={i} className={`inline-block text-xs px-2 py-0.5 rounded border ${tagColors[tag]} mr-1 mb-1`}>
          <strong>[{tag}]</strong> {content}
        </span>
      );
    }
    return part ? <span key={i} className="text-sm text-[#24292f]">{part}</span> : null;
  });
}

/* ═══════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════════ */
function CompetencySet() {
  const { canManageCompetencies } = useUserRole();
  const [toast, setToast] = useState(null);

  /* ── competency selection ── */
  const [competencies, setCompetencies] = useState([]);
  const [selectedCompetency, setSelectedCompetency] = useState(null);
  const [compSearch, setCompSearch] = useState("");

  /* ── sets state ── */
  const [sets, setSets] = useState([]);
  const [setsLoading, setSetsLoading] = useState(false);
  const [setForm, setSetForm] = useState(null);  // null = closed
  const [editingSetId, setEditingSetId] = useState(null);
  const [saving, setSaving] = useState(false);

  /* ── questions state ── */
  const [expandedSetId, setExpandedSetId] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [questionForm, setQuestionForm] = useState(null);  // null = closed
  const [editingQuestionId, setEditingQuestionId] = useState(null);

  /* ── delete confirm ── */
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  /* ── loaders ── */
  const loadCompetencies = useCallback(async () => {
    try {
      const data = await fetchCompetencies();
      setCompetencies(data);
    } catch (err) {
      console.error("Failed to load competencies", err);
    }
  }, []);

  const loadSets = useCallback(async (code) => {
    setSetsLoading(true);
    try {
      const data = await fetchSetsForCompetency(code);
      setSets(data);
    } catch (err) {
      console.error(err);
      setSets([]);
    } finally {
      setSetsLoading(false);
    }
  }, []);

  const loadQuestions = useCallback(async (setId) => {
    setQuestionsLoading(true);
    try {
      const data = await fetchQuestionsForSet(setId);
      setQuestions(data);
    } catch (err) {
      console.error(err);
      setQuestions([]);
    } finally {
      setQuestionsLoading(false);
    }
  }, []);

  useEffect(() => { loadCompetencies(); }, [loadCompetencies]);

  useEffect(() => {
    if (selectedCompetency) loadSets(selectedCompetency.code);
    else setSets([]);
  }, [selectedCompetency, loadSets]);

  useEffect(() => {
    if (expandedSetId) loadQuestions(expandedSetId);
    else setQuestions([]);
  }, [expandedSetId, loadQuestions]);

  /* ── filtered competencies ── */
  const filteredComps = compSearch
    ? competencies.filter(
        (c) =>
          c.code.toLowerCase().includes(compSearch.toLowerCase()) ||
          c.name.toLowerCase().includes(compSearch.toLowerCase())
      )
    : competencies;

  /* ── Set CRUD handlers ── */
  const openSetCreate = () => {
    setSetForm({ title: "", description: "", instructions: "", order: 0, resources: [], stop_rule: "" });
    setEditingSetId(null);
  };

  const openSetEdit = (s) => {
    setSetForm({
      title: s.title,
      description: s.description || "",
      instructions: s.instructions || "",
      order: s.order || 0,
      resources: s.resources || [],
      stop_rule: s.stop_rule || "",
    });
    setEditingSetId(s.id);
  };

  const handleSetSave = async () => {
    if (!setForm.title.trim()) {
      setToast({ type: "error", message: "Title is required" });
      return;
    }
    setSaving(true);
    try {
      // Filter out resources with empty URLs
      const validResources = (setForm.resources || []).filter(r => r.url && r.url.trim());
      const payload = {
        title: setForm.title,
        description: setForm.description,
        instructions: setForm.instructions,
        order: setForm.order,
        resources: validResources,
        stop_rule: setForm.stop_rule || null,
      };
      if (editingSetId) {
        await updateSet(editingSetId, payload);
        setToast({ type: "success", message: "Set updated" });
      } else {
        await createSet({ ...payload, competency_code: selectedCompetency.code });
        setToast({ type: "success", message: "Set created" });
      }
      setSetForm(null);
      loadSets(selectedCompetency.code);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(d => d.msg || d).join(", ") : (detail || "Failed to save set");
      setToast({ type: "error", message: msg });
    } finally {
      setSaving(false);
    }
  };

  const handleSetDelete = async (id) => {
    try {
      await deleteSet(id);
      setDeleteConfirm(null);
      setToast({ type: "success", message: "Set deleted" });
      loadSets(selectedCompetency.code);
    } catch (err) {
      setToast({ type: "error", message: err.response?.data?.detail || "Failed to delete" });
    }
  };

  /* ── Question CRUD handlers ── */
  const EMPTY_MCQ = { text: "", answer: "", order: 0, type: "mcq", options: [{ text: "Correct", is_correct: true }, { text: "Wrong", is_correct: false }, { text: "Skipped", is_correct: false }], grid_config: null };
  const EMPTY_GRID = { text: "", description: "", order: 0, type: "grid", options: [], grid_config: { passage_text: "", columns: 5, rows: null, cells: [], is_timed: false, time_limit_seconds: null } };

  const openQuestionCreate = (type) => {
    setQuestionForm(type === "grid" ? { ...EMPTY_GRID } : { ...EMPTY_MCQ });
    setEditingQuestionId(null);
  };

  const openQuestionEdit = (q) => {
    setQuestionForm({
      text: q.text,
      answer: q.answer || q.description || "",
      order: q.order || 0,
      type: q.type,
      options: q.options || [],
      grid_config: q.grid_config || null,
    });
    setEditingQuestionId(q.id);
  };

  const handleQuestionSave = async () => {
    if (!questionForm.text.trim()) {
      setToast({ type: "error", message: "Question text is required" });
      return;
    }
    setSaving(true);
    try {
      // Map 'answer' field to 'description' for backend
      const payload = { ...questionForm };
      if (payload.answer !== undefined) {
        payload.description = payload.answer;
        delete payload.answer;
      }
      
      if (editingQuestionId) {
        await updateQuestion(editingQuestionId, payload);
        setToast({ type: "success", message: "Question updated" });
      } else {
        await createQuestion({ ...payload, set_id: expandedSetId });
        setToast({ type: "success", message: "Question created" });
      }
      setQuestionForm(null);
      loadQuestions(expandedSetId);
    } catch (err) {
      setToast({ type: "error", message: err.response?.data?.detail || "Failed to save question" });
    } finally {
      setSaving(false);
    }
  };

  const handleQuestionDelete = async (id) => {
    try {
      await deleteQuestion(id);
      setDeleteConfirm(null);
      setToast({ type: "success", message: "Question deleted" });
      loadQuestions(expandedSetId);
    } catch (err) {
      setToast({ type: "error", message: err.response?.data?.detail || "Failed to delete" });
    }
  };

  /* ── add MCQ option ── */
  const addOption = () => {
    setQuestionForm({
      ...questionForm,
      options: [...questionForm.options, { text: "", is_correct: false }],
    });
  };

  const removeOption = (idx) => {
    setQuestionForm({
      ...questionForm,
      options: questionForm.options.filter((_, i) => i !== idx),
    });
  };

  const updateOption = (idx, field, value) => {
    const opts = [...questionForm.options];
    opts[idx] = { ...opts[idx], [field]: value };
    setQuestionForm({ ...questionForm, options: opts });
  };

  /* ── resource handling ── */
  const addResource = () => {
    setSetForm({
      ...setForm,
      resources: [...setForm.resources, { url: "", file_type: "image", file_size: null, label: "", uploading: false }],
    });
  };

  const removeResource = (idx) => {
    setSetForm({
      ...setForm,
      resources: setForm.resources.filter((_, i) => i !== idx),
    });
  };

  const updateResource = (idx, field, value) => {
    const res = [...setForm.resources];
    res[idx] = { ...res[idx], [field]: value };
    setSetForm({ ...setForm, resources: res });
  };

  const handleFileUpload = async (idx, file) => {
    if (!file) return;

    // Update uploading state
    const res = [...setForm.resources];
    res[idx] = { ...res[idx], uploading: true };
    setSetForm({ ...setForm, resources: res });

    try {
      const formData = new FormData();
      formData.append("file", file);

      const data = await uploadSetResource(file);

      // Update resource with URL and metadata
      const updatedRes = [...setForm.resources];
      updatedRes[idx] = {
        url: data.url,
        file_type: data.file_type?.split("/")[0] || "other",
        file_size: data.file_size,
        label: updatedRes[idx].label || data.filename,
        uploading: false,
      };
      setSetForm({ ...setForm, resources: updatedRes });
      setToast({ type: "success", message: "File uploaded successfully" });
    } catch (err) {
      console.error(err);
      const updatedRes = [...setForm.resources];
      updatedRes[idx] = { ...updatedRes[idx], uploading: false };
      setSetForm({ ...setForm, resources: updatedRes });
      setToast({ type: "error", message: "Failed to upload file" });
    }
  };

  

  /* ═══════════════════════════════════════════════════════════
     RENDER
     ═══════════════════════════════════════════════════════════ */
  return (
    <div className="p-6">
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}

      <div className="mb-6">
        <h1 className="text-xl font-bold text-[#24292f]">Competency Sets & Questions</h1>
        <p className="text-sm text-[#57606a] mt-1">
          Manage assessment sets and questions for each competency
        </p>
      </div>

      <div className="flex gap-6">
        {/* ── LEFT: Competency Picker ── */}
        <div className="w-72 flex-shrink-0">
          <div className="bg-white rounded-xl border border-[#e5e7eb] overflow-hidden">
            <div className="p-3 border-b border-[#e5e7eb]">
              <div className="relative">
                <HiOutlineSearch className="absolute left-3 top-2.5 text-[#8b949e] text-sm" />
                <input
                  type="text"
                  className="w-full pl-9 pr-3 py-2 text-sm border border-[#d0d5dd] rounded-md focus:outline-none focus:ring-2 focus:ring-[#0078d4]/10 focus:border-[#0078d4]"
                  placeholder="Search competency…"
                  value={compSearch}
                  onChange={(e) => setCompSearch(e.target.value)}
                />
              </div>
            </div>
            <div className="max-h-[calc(100vh-260px)] overflow-y-auto">
              {filteredComps.length === 0 ? (
                <p className="text-sm text-[#8b949e] p-4 text-center">No competencies</p>
              ) : (
                filteredComps.map((c) => (
                  <button
                    key={c.code}
                    onClick={() => {
                      setSelectedCompetency(c);
                      setExpandedSetId(null);
                      setSetForm(null);
                      setQuestionForm(null);
                    }}
                    className={`w-full text-left px-4 py-2.5 border-b border-[#f0f0f0] text-sm hover:bg-[#f6f8fa] transition-colors ${
                      selectedCompetency?.code === c.code ? "bg-[#eff6ff] border-l-2 border-l-[#0078d4]" : ""
                    }`}
                  >
                    <code className="text-xs text-[#0078d4] font-mono">{c.code}</code>
                    <span className="block text-[#24292f] text-xs mt-0.5 truncate">{c.name}</span>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>

        {/* ── RIGHT: Sets & Questions ── */}
        <div className="flex-1 min-w-0">
          {!selectedCompetency ? (
            <div className="bg-white rounded-xl border border-[#e5e7eb] p-12 text-center">
              <p className="text-[#8b949e] text-sm">Select a competency from the left panel to manage its sets & questions</p>
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-base font-semibold text-[#24292f]">
                    Sets for <code className="text-[#0078d4]">{selectedCompetency.code}</code>
                  </h2>
                  <p className="text-xs text-[#57606a]">{selectedCompetency.name}</p>
                </div>
                {canManageCompetencies && !setForm && (
                  <button className={btnPrimary} onClick={openSetCreate}>
                    <HiOutlinePlus className="text-sm" /> Add Set
                  </button>
                )}
              </div>

              {/* ═══ SET FORM ═══ */}
              {setForm && (
                <div className="bg-white rounded-xl border border-[#e5e7eb] p-5 mb-4">
                  <h3 className="text-sm font-semibold text-[#24292f] mb-4">
                    {editingSetId ? "Edit Set" : "Create Set"}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className={labelClass}>Title <span className="text-red-500">*</span></label>
                      <input type="text" value={setForm.title} onChange={(e) => setSetForm({ ...setForm, title: e.target.value })} className={inputClass} placeholder="Set title" />
                    </div>
                    <div>
                      <label className={labelClass}>Order</label>
                      <input type="number" value={setForm.order} onChange={(e) => setSetForm({ ...setForm, order: parseInt(e.target.value) || 0 })} className={inputClass} />
                    </div>
                    <div className="md:col-span-2">
                      <label className={labelClass}>Stop Rule</label>
                      <select value={setForm.stop_rule} onChange={(e) => setSetForm({ ...setForm, stop_rule: e.target.value })} className={selectClass}>
                        <option value="">None</option>
                        <option value="3_consecutive_wrong">3 Consecutive Wrong → Stop Set</option>
                        <option value="4_consecutive_wrong">4 Consecutive Wrong → Stop Set</option>
                      </select>
                      <p className="text-[10px] text-[#8b949e] mt-1">If set, the assessment will stop this set after N consecutive wrong answers.</p>
                    </div>
                    <div className="md:col-span-2">
                      <label className={labelClass}>Description</label>
                      <textarea rows={2} value={setForm.description} onChange={(e) => setSetForm({ ...setForm, description: e.target.value })} className={inputClass} placeholder="Brief description…" />
                    </div>
                    <div className="md:col-span-2">
                      <label className={labelClass}>
                        Instructions
                        <span className="text-xs text-[#8b949e] ml-2 font-normal">
                          Use [Instructor]…[/Instructor], [Student]…[/Student], [Rules]…[/Rules] tags
                        </span>
                      </label>
                      <textarea rows={4} value={setForm.instructions} onChange={(e) => setSetForm({ ...setForm, instructions: e.target.value })} className={`${inputClass} font-mono text-xs`} placeholder="[Student]Read aloud from the grid[/Student]&#10;[Instructor]Mark cells as student reads[/Instructor]&#10;[Rules]Time limit: 60 seconds[/Rules]" />
                      {setForm.instructions && (
                        <div className="mt-2 p-2 bg-[#f6f8fa] rounded border border-[#e5e7eb]">
                          <p className="text-[10px] text-[#8b949e] mb-1">Preview:</p>
                          <div className="flex flex-wrap">{renderInstructionPreview(setForm.instructions)}</div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Resources */}
                  <div className="mt-4 pt-4 border-t border-[#e5e7eb]">
                    <div className="flex items-center justify-between mb-2">
                      <label className={labelClass}>Resources / Attachments</label>
                      <button type="button" onClick={addResource} className="text-xs text-[#0078d4] hover:underline flex items-center gap-1">
                        <HiOutlinePhotograph className="text-sm" /> Add Resource
                      </button>
                    </div>
                    {setForm.resources.map((res, idx) => (
                      <div key={idx} className="flex items-center gap-2 mb-2">
                        {/* File upload or URL input */}
                        <div className="flex-1 flex gap-2">
                          <input
                            type="file"
                            id={`file-upload-${idx}`}
                            className="hidden"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) handleFileUpload(idx, file);
                            }}
                            accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
                          />
                          <label
                            htmlFor={`file-upload-${idx}`}
                            className={`px-3 py-2 text-xs border border-[#d0d7de] rounded-lg cursor-pointer hover:bg-[#f6f8fa] transition-all flex items-center gap-1 ${res.uploading ? 'opacity-50 pointer-events-none' : ''}`}
                          >
                            <HiOutlinePhotograph className="text-sm" />
                            {res.uploading ? 'Uploading...' : 'Choose File'}
                          </label>
                          <input
                            type="text"
                            placeholder="Or enter URL manually"
                            value={res.url}
                            onChange={(e) => updateResource(idx, "url", e.target.value)}
                            className={`${inputClass} flex-1`}
                            disabled={res.uploading}
                          />
                        </div>
                        <select
                          value={res.file_type || "image"}
                          onChange={(e) => updateResource(idx, "file_type", e.target.value)}
                          className="bg-white border border-[#d0d7de] rounded-lg px-2 py-2 text-xs"
                          disabled={res.uploading}
                        >
                          <option value="image">Image</option>
                          <option value="video">Video</option>
                          <option value="pdf">PDF</option>
                          <option value="audio">Audio</option>
                          <option value="doc">Document</option>
                          <option value="spreadsheet">Spreadsheet</option>
                          <option value="presentation">Presentation</option>
                          <option value="other">Other</option>
                        </select>
                        <input
                          type="text"
                          placeholder="Label"
                          value={res.label || ""}
                          onChange={(e) => updateResource(idx, "label", e.target.value)}
                          className={`${inputClass} w-32`}
                          disabled={res.uploading}
                        />
                        <button
                          onClick={() => removeResource(idx)}
                          className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                          disabled={res.uploading}
                        >
                          <HiOutlineTrash className="text-sm" />
                        </button>
                      </div>
                    ))}
                  </div>

                  <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-[#e5e7eb]">
                    <button onClick={() => setSetForm(null)} className={btnSecondary}>Cancel</button>
                    <button onClick={handleSetSave} disabled={saving} className={`${btnPrimary} disabled:opacity-40`}>
                      {saving ? "Saving…" : editingSetId ? "Update Set" : "Create Set"}
                    </button>
                  </div>
                </div>
              )}

              {/* ═══ SETS LIST ═══ */}
              {setsLoading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="bg-white rounded-xl border border-[#e5e7eb] p-4 animate-pulse">
                      <div className="h-4 bg-[#f0f0f0] rounded w-1/3 mb-2" />
                      <div className="h-3 bg-[#f0f0f0] rounded w-2/3" />
                    </div>
                  ))}
                </div>
              ) : sets.length === 0 && !setForm ? (
                <div className="bg-white rounded-xl border border-[#e5e7eb] p-8 text-center">
                  <p className="text-[#8b949e] text-sm">No sets yet. Create one to start adding questions.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {sets.map((s) => (
                    <div key={s.id} className="bg-white rounded-xl border border-[#e5e7eb] overflow-hidden">
                      {/* Set header row */}
                      <div
                        className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-[#f6f8fa] transition-colors"
                        onClick={() => {
                          setExpandedSetId(expandedSetId === s.id ? null : s.id);
                          setQuestionForm(null);
                        }}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          {expandedSetId === s.id ? (
                            <HiOutlineChevronDown className="text-sm text-[#57606a] flex-shrink-0" />
                          ) : (
                            <HiOutlineChevronRight className="text-sm text-[#57606a] flex-shrink-0" />
                          )}
                          <div className="min-w-0">
                            <span className="text-sm font-medium text-[#24292f]">{s.title}</span>
                            <span className="ml-2 text-[10px] px-1.5 py-0.5 bg-[#f6f8fa] text-[#57606a] rounded">
                              {s.question_count} question{s.question_count !== 1 ? "s" : ""}
                            </span>
                            {s.stop_rule && (
                              <span className="ml-1 text-[10px] px-1.5 py-0.5 bg-red-50 text-red-600 rounded">
                                Rule: {s.stop_rule === "3_consecutive_wrong" ? "3 wrong" : "4 wrong"}
                              </span>
                            )}
                            {s.resources && s.resources.length > 0 && (
                              <span className="ml-1 text-[10px] px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded">
                                {s.resources.length} resource{s.resources.length !== 1 ? "s" : ""}
                              </span>
                            )}
                          </div>
                        </div>
                        {canManageCompetencies && (
                          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                            <button title="Edit" onClick={() => openSetEdit(s)} className="p-1.5 text-[#57606a] hover:text-[#0078d4] hover:bg-[#eff6ff] rounded-md transition-all">
                              <HiOutlinePencil className="text-sm" />
                            </button>
                            <button title="Delete" onClick={() => setDeleteConfirm({ type: "set", item: s })} className="p-1.5 text-[#57606a] hover:text-red-600 hover:bg-red-50 rounded-md transition-all">
                              <HiOutlineTrash className="text-sm" />
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Instructions preview */}
                      {s.instructions && expandedSetId !== s.id && (
                        <div className="px-4 pb-2">
                          <div className="flex flex-wrap">{renderInstructionPreview(s.instructions)}</div>
                        </div>
                      )}

                      {/* ═══ EXPANDED: Questions ═══ */}
                      {expandedSetId === s.id && (
                        <div className="border-t border-[#e5e7eb] bg-[#fafbfc]">
                          {/* Instructions full */}
                          {s.instructions && (
                            <div className="px-4 py-2 border-b border-[#e5e7eb]">
                              <p className="text-[10px] font-semibold text-[#8b949e] mb-1">INSTRUCTIONS</p>
                              <div className="flex flex-wrap">{renderInstructionPreview(s.instructions)}</div>
                            </div>
                          )}

                          {/* Resources */}
                          {s.resources && s.resources.length > 0 && (
                            <div className="px-4 py-2 border-b border-[#e5e7eb]">
                              <p className="text-[10px] font-semibold text-[#8b949e] mb-1">RESOURCES</p>
                              <div className="flex flex-wrap gap-2">
                                {s.resources.map((res, ri) => (
                                  <a key={ri} href={res.url} target="_blank" rel="noopener noreferrer"
                                    className="text-xs px-2 py-1 bg-white border border-[#d0d7de] rounded hover:bg-[#f6f8fa] flex items-center gap-1"
                                  >
                                    <HiOutlinePhotograph className="text-[#57606a]" />
                                    {res.label || res.file_type || "Resource"}
                                  </a>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Question toolbar */}
                          {canManageCompetencies && !questionForm && (
                            <div className="px-4 py-2 border-b border-[#e5e7eb] flex items-center gap-2">
                              <span className="text-xs text-[#57606a]">Add question:</span>
                              <button onClick={() => openQuestionCreate("mcq")} className="text-xs px-3 py-1 bg-[#0078d4] text-white rounded-md hover:bg-[#106ebe] transition-all">
                                + MCQ
                              </button>
                              <button onClick={() => openQuestionCreate("grid")} className="text-xs px-3 py-1 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 transition-all">
                                + Grid
                              </button>
                            </div>
                          )}

                          {/* ═══ QUESTION FORM ═══ */}
                          {questionForm && (
                            <div className="m-3 p-4 bg-white rounded-lg border border-[#e5e7eb]">
                              <h4 className="text-sm font-semibold text-[#24292f] mb-3">
                                {editingQuestionId ? "Edit" : "Create"} {questionForm.type.toUpperCase()} Question
                              </h4>
                              <div className="space-y-3">
                                <div>
                                  <label className={labelClass}>Question Text <span className="text-red-500">*</span></label>
                                  <textarea rows={2} value={questionForm.text} onChange={(e) => setQuestionForm({ ...questionForm, text: e.target.value })} className={inputClass} placeholder="Enter question / prompt text…" />
                                </div>
                                
                                {/* Answer field for MCQ type */}
                                {questionForm.type === "mcq" && (
                                  <div>
                                    <label className={labelClass}>Answer <span className="text-gray-500 text-xs">(e.g., for '2+2', answer is '4')</span></label>
                                    <input 
                                      type="text" 
                                      value={questionForm.answer || ""} 
                                      onChange={(e) => setQuestionForm({ ...questionForm, answer: e.target.value })} 
                                      className={inputClass} 
                                      placeholder="Enter the correct answer" 
                                    />
                                  </div>
                                )}
                                
                                <div>
                                  <label className={labelClass}>Order</label>
                                  <input type="number" value={questionForm.order} onChange={(e) => setQuestionForm({ ...questionForm, order: parseInt(e.target.value) || 0 })} className={inputClass} />
                                </div>

                                {/* MCQ Options */}
                                {questionForm.type === "mcq" && (
                                  <div>
                                    <div className="flex items-center justify-between mb-2">
                                      <label className={labelClass}>Options</label>
                                      <button type="button" onClick={addOption} className="text-xs text-[#0078d4] hover:underline">+ Add Option</button>
                                    </div>
                                    {questionForm.options.map((opt, idx) => (
                                      <div key={idx} className="flex items-center gap-2 mb-2">
                                        <input
                                          type="checkbox"
                                          checked={opt.is_correct}
                                          onChange={(e) => updateOption(idx, "is_correct", e.target.checked)}
                                          className="rounded border-[#d0d7de]"
                                          title="Mark as correct"
                                        />
                                        <input
                                          type="text"
                                          value={opt.text}
                                          onChange={(e) => updateOption(idx, "text", e.target.value)}
                                          className={`${inputClass} flex-1`}
                                          placeholder={`Option ${idx + 1}`}
                                        />
                                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${opt.is_correct ? "bg-green-50 text-green-700" : "bg-red-50 text-red-600"}`}>
                                          {opt.is_correct ? "Correct" : "Wrong"}
                                        </span>
                                        {questionForm.options.length > 2 && (
                                          <button onClick={() => removeOption(idx)} className="p-1 text-red-400 hover:text-red-600"><HiOutlineX className="text-xs" /></button>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                )}

                                {/* Grid Config */}
                                {questionForm.type === "grid" && (
                                  <div className="space-y-3">
                                    <div>
                                      <label className={labelClass}>Passage Text</label>
                                      <textarea
                                        rows={3}
                                        value={questionForm.grid_config?.passage_text || ""}
                                        onChange={(e) => setQuestionForm({
                                          ...questionForm,
                                          grid_config: { ...questionForm.grid_config, passage_text: e.target.value },
                                        })}
                                        className={inputClass}
                                        placeholder="Enter passage text (words will auto-map to grid cells)…"
                                      />
                                    </div>
                                    <div className="grid grid-cols-3 gap-3">
                                      <div>
                                        <label className={labelClass}>Columns</label>
                                        <input
                                          type="number" min={1} max={20}
                                          value={questionForm.grid_config?.columns || 5}
                                          onChange={(e) => setQuestionForm({
                                            ...questionForm,
                                            grid_config: { ...questionForm.grid_config, columns: parseInt(e.target.value) || 5 },
                                          })}
                                          className={inputClass}
                                        />
                                      </div>
                                      <div>
                                        <label className={labelClass}>Timed</label>
                                        <div className="flex items-center gap-2 mt-1">
                                          <button
                                            type="button"
                                            onClick={() => setQuestionForm({
                                              ...questionForm,
                                              grid_config: { ...questionForm.grid_config, is_timed: !questionForm.grid_config?.is_timed },
                                            })}
                                            className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs border transition-all ${
                                              questionForm.grid_config?.is_timed
                                                ? "bg-amber-50 border-amber-300 text-amber-700"
                                                : "bg-white border-[#d0d7de] text-[#57606a]"
                                            }`}
                                          >
                                            <HiOutlineClock className="text-sm" />
                                            {questionForm.grid_config?.is_timed ? "Timed" : "Not Timed"}
                                          </button>
                                        </div>
                                      </div>
                                      {questionForm.grid_config?.is_timed && (
                                        <div>
                                          <label className={labelClass}>Time (seconds)</label>
                                          <input
                                            type="number" min={1}
                                            value={questionForm.grid_config?.time_limit_seconds || ""}
                                            onChange={(e) => setQuestionForm({
                                              ...questionForm,
                                              grid_config: { ...questionForm.grid_config, time_limit_seconds: parseInt(e.target.value) || null },
                                            })}
                                            className={inputClass}
                                            placeholder="60"
                                          />
                                        </div>
                                      )}
                                    </div>

                                    {/* Grid Preview */}
                                    {questionForm.grid_config?.passage_text && (
                                      <div>
                                        <label className={labelClass}>Grid Preview</label>
                                        <div className="border border-[#d0d7de] rounded-lg p-3 bg-[#f6f8fa] overflow-x-auto">
                                          <div
                                            className="grid gap-1"
                                            style={{
                                              gridTemplateColumns: `repeat(${questionForm.grid_config.columns || 5}, minmax(0, 1fr))`,
                                            }}
                                          >
                                            {questionForm.grid_config.passage_text.split(/\s+/).filter(Boolean).map((word, i) => (
                                              <div
                                                key={i}
                                                className="px-2 py-1.5 bg-white border border-[#e5e7eb] rounded text-xs text-center font-mono truncate"
                                                title={word}
                                              >
                                                {word}
                                              </div>
                                            ))}
                                          </div>
                                          <p className="text-[10px] text-[#8b949e] mt-2">
                                            {questionForm.grid_config.passage_text.split(/\s+/).filter(Boolean).length} cells •{" "}
                                            {questionForm.grid_config.columns || 5} columns •{" "}
                                            {Math.ceil(questionForm.grid_config.passage_text.split(/\s+/).filter(Boolean).length / (questionForm.grid_config.columns || 5))} rows
                                          </p>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                              <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-[#e5e7eb]">
                                <button onClick={() => setQuestionForm(null)} className={btnSecondary}>Cancel</button>
                                <button onClick={handleQuestionSave} disabled={saving} className={`${btnPrimary} disabled:opacity-40`}>
                                  {saving ? "Saving…" : editingQuestionId ? "Update Question" : "Create Question"}
                                </button>
                              </div>
                            </div>
                          )}

                          {/* Questions list */}
                          <div className="p-3 space-y-2">
                            {questionsLoading ? (
                              [1, 2].map((i) => (
                                <div key={i} className="bg-white rounded-lg border border-[#e5e7eb] p-3 animate-pulse">
                                  <div className="h-3 bg-[#f0f0f0] rounded w-1/2 mb-2" />
                                  <div className="h-3 bg-[#f0f0f0] rounded w-3/4" />
                                </div>
                              ))
                            ) : questions.length === 0 && !questionForm ? (
                              <p className="text-xs text-[#8b949e] text-center py-4">No questions in this set yet.</p>
                            ) : (
                              questions.map((q) => (
                                <div key={q.id} className="bg-white rounded-lg border border-[#e5e7eb] p-3">
                                  <div className="flex items-start justify-between">
                                    <div className="min-w-0 flex-1">
                                      <div className="flex items-center gap-2 mb-1">
                                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${
                                          q.type === "mcq" ? "bg-blue-50 text-blue-700" : "bg-emerald-50 text-emerald-700"
                                        }`}>
                                          {q.type.toUpperCase()}
                                        </span>
                                        <span className="text-[10px] text-[#8b949e]">#{q.order}</span>
                                        {q.type === "grid" && q.grid_config?.is_timed && (
                                          <span className="text-[10px] px-1.5 py-0.5 bg-amber-50 text-amber-700 rounded flex items-center gap-0.5">
                                            <HiOutlineClock className="text-xs" /> {q.grid_config.time_limit_seconds}s
                                          </span>
                                        )}
                                      </div>
                                      <p className="text-sm text-[#24292f] font-medium">{q.text}</p>
                                      {q.type === "mcq" && (q.answer || q.description) && (
                                        <p className="text-xs text-[#57606a] mt-1">Answer: {q.answer || q.description}</p>
                                      )}

                                      {/* MCQ options display */}
                                      {q.type === "mcq" && q.options && (
                                        <div className="flex flex-wrap gap-1 mt-2">
                                          {q.options.map((opt, oi) => (
                                            <span key={oi} className={`text-[10px] px-2 py-0.5 rounded ${opt.is_correct ? "bg-green-50 text-green-700" : "bg-gray-50 text-gray-600"}`}>
                                              {opt.text} {opt.is_correct ? "✓" : ""}
                                            </span>
                                          ))}
                                        </div>
                                      )}

                                      {/* Grid info display */}
                                      {q.type === "grid" && q.grid_config && (
                                        <div className="mt-2 text-[10px] text-[#57606a]">
                                          {q.grid_config.cells?.length || 0} cells • {q.grid_config.columns} cols
                                        </div>
                                      )}
                                    </div>
                                    {canManageCompetencies && (
                                      <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                                        <button title="Edit" onClick={() => openQuestionEdit(q)} className="p-1.5 text-[#57606a] hover:text-[#0078d4] hover:bg-[#eff6ff] rounded-md transition-all">
                                          <HiOutlinePencil className="text-sm" />
                                        </button>
                                        <button title="Delete" onClick={() => setDeleteConfirm({ type: "question", item: q })} className="p-1.5 text-[#57606a] hover:text-red-600 hover:bg-red-50 rounded-md transition-all">
                                          <HiOutlineTrash className="text-sm" />
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ═══ DELETE CONFIRM MODAL ═══ */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-5 text-center">
            <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-red-100 flex items-center justify-center">
              <HiOutlineExclamation className="text-2xl text-red-600" />
            </div>
            <h3 className="text-base font-semibold text-[#24292f] mb-1">
              Delete {deleteConfirm.type === "set" ? "Set" : "Question"}?
            </h3>
            <p className="text-sm text-[#57606a] mb-4">
              {deleteConfirm.type === "set"
                ? `This will delete "${deleteConfirm.item.title}" and all its questions.`
                : `This will delete this ${deleteConfirm.item.type.toUpperCase()} question.`}
            </p>
            <div className="flex justify-center gap-2">
              <button onClick={() => setDeleteConfirm(null)} className={btnSecondary}>Cancel</button>
              <button
                onClick={() =>
                  deleteConfirm.type === "set"
                    ? handleSetDelete(deleteConfirm.item.id)
                    : handleQuestionDelete(deleteConfirm.item.id)
                }
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-all"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CompetencySet;
